import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Download, ArrowLeft, AlertCircle } from 'lucide-react';

export default function StatementDownloadPage() {
  const { token } = useParams();
  const [error, setError] = useState(null);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid statement link. Token is missing.');
      return;
    }

    const downloadStatement = async () => {
      try {
        // Get backend URL from environment or construct it
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://twachanga.co.ke/api';
        const backendUrl = apiBaseUrl.replace(/\/api\/?$/, '');
        
        // For local development without env var, try to detect backend port
        let finalBackendUrl = backendUrl;
        if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && !process.env.REACT_APP_API_BASE_URL) {
          // In local dev fallback, backend is usually on port 8000
          finalBackendUrl = window.location.origin.replace(':3000', ':8000');
        }
        
        const statementUrl = `${finalBackendUrl}/api/contributions/member-statement/${token}`;
        
        // Fetch the PDF as a blob
        const response = await fetch(statementUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          // Try to parse as JSON first, fallback to text if not JSON
          let errorMessage = 'Failed to download statement';
          const contentType = response.headers.get('content-type');
          
          if (contentType && contentType.includes('application/json')) {
            try {
              const errorData = await response.json();
              // Provide user-friendly error messages
              if (response.status === 404) {
                errorMessage = 'This statement link has expired or is invalid. Statement links are valid for 3 months from the date they were sent. Please request a new statement link from your group administrator.';
              } else if (response.status === 403) {
                errorMessage = 'Access denied. This statement link may have expired or is invalid. Statement links are valid for 3 months. Please contact your group administrator for a new link.';
              } else {
                errorMessage = errorData.message || 'Failed to download statement. Please try again or contact support.';
              }
            } catch (e) {
              // If JSON parsing fails, use default message
              if (response.status === 404 || response.status === 403) {
                errorMessage = 'This statement link has expired or is invalid. Statement links are valid for 3 months from the date they were sent. Please request a new statement link from your group administrator.';
              }
            }
          } else {
            // If response is not JSON (e.g., HTML redirect), provide helpful message
            // This should never happen as we're calling the API directly, but handle it just in case
            if (response.status === 403 || response.status === 404) {
              errorMessage = 'This statement link has expired or is invalid. Statement links are valid for 3 months from the date they were sent. Please request a new statement link from your group administrator.';
            } else {
              errorMessage = 'Unable to download statement. Please try again or contact support.';
            }
          }
          
          throw new Error(errorMessage);
        }

        // Get the blob
        const blob = await response.blob();
        
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'contribution-statement.pdf'; // fallback
        if (contentDisposition) {
          // Try to match filename="..." or filename*=UTF-8''... format
          // Laravel's download() method sets: attachment; filename="statement-Name-2025-05-16-to-2025-11-16.pdf"
          const quotedMatch = contentDisposition.match(/filename="([^"]+)"/);
          const unquotedMatch = contentDisposition.match(/filename=([^;]+)/);
          const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          
          if (quotedMatch && quotedMatch[1]) {
            filename = quotedMatch[1];
          } else if (utf8Match && utf8Match[1]) {
            // Decode URL-encoded filename (e.g., Ernest%20Kiromo -> Ernest Kiromo)
            filename = decodeURIComponent(utf8Match[1]);
          } else if (unquotedMatch && unquotedMatch[1]) {
            filename = unquotedMatch[1].trim();
          }
        }
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        setDownloadUrl({ url, filename });
        
        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Mark as complete after a short delay
        setTimeout(() => {
          setDownloadComplete(true);
        }, 1000);
        
      } catch (err) {
        console.error('Download error:', err);
        // Ensure we never redirect - always show error on frontend
        // Provide user-friendly error message
        if (err.message && (err.message.includes('expired') || err.message.includes('invalid'))) {
          setError(err.message);
        } else if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
          setError('Unable to connect to the server. Please check your internet connection and try again. If the problem persists, please contact your group administrator.');
        } else {
          setError(err.message || 'Failed to download statement. Statement links are valid for 3 months. If this link has expired, please request a new statement link from your group administrator.');
        }
      }
    };

    downloadStatement();
  }, [token]);

  const handleDownloadAgain = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl.url;
      link.download = downloadUrl.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Download Failed</h2>
          <div className="text-gray-600 mb-6 space-y-2">
            <p>{error}</p>
            {error.includes('expired') || error.includes('invalid') ? (
              <p className="text-sm text-gray-500 mt-2">
                Statement links are valid for 3 months from the date they were sent.
              </p>
            ) : null}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                // Reload the page to try again - stay on frontend, never redirect
                window.location.reload();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/transactions"
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors inline-flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (downloadComplete) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Download Complete!</h2>
          <p className="text-gray-600 mb-6">
            Your contribution statement has been downloaded successfully.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleDownloadAgain}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Again
            </button>
            <Link
              to="/transactions"
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors inline-flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Preparing Your Statement</h2>
        <p className="text-gray-600">Your contribution statement is being prepared for download...</p>
      </div>
    </div>
  );
}

